/**
 * Hook para escutar eventos de presença (digitando/gravando) de um contato
 * 
 * Funciona com Supabase Realtime broadcast no canal `presence:{inboxId}`.
 * Os eventos são enviados pelos webhooks dos providers (z-api-webhook, waha-webhook).
 * 
 * IMPORTANTE: Os telefones são normalizados (apenas dígitos) para garantir
 * match correto entre o payload do webhook e o contato sendo visualizado.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { isPresenceSupported } from '@/lib/presenceConfig';

// Logger para eventos de presença (só aparece em desenvolvimento)
const presenceLogger = createLogger('ContactPresence');

export interface PresenceState {
  isTyping: boolean;
  isRecording: boolean;
}

export function useContactPresence(
  inboxId: string | null, 
  phone: string | null,
  providerSlug: string | null | undefined
): PresenceState {
  const [presence, setPresence] = useState<PresenceState>({ 
    isTyping: false, 
    isRecording: false 
  });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Normalizar telefone do contato (remover caracteres não-numéricos)
  // Isso garante match com o payload do webhook que também é normalizado
  const normalizedContactPhone = useMemo(() => {
    return phone?.replace(/\D/g, '') || null;
  }, [phone]);

  useEffect(() => {
    // Se provider não suporta presença, não criar canal Realtime desnecessário
    if (!isPresenceSupported(providerSlug)) {
      setPresence({ isTyping: false, isRecording: false });
      return;
    }

    if (!inboxId || !normalizedContactPhone) {
      setPresence({ isTyping: false, isRecording: false });
      return;
    }

    const channelName = `presence:${inboxId}`;
    presenceLogger.debug('Inscrevendo no canal de presença', { 
      channelName, 
      phone: normalizedContactPhone.substring(0, 6) + '...' 
    });
    
    const channel = supabase.channel(channelName)
      .on('broadcast', { event: 'presence' }, (payload) => {
        // Normalizar telefone do payload para comparação
        const payloadPhone = payload.payload?.phone?.replace(/\D/g, '');
        
        presenceLogger.debug('Evento de presença recebido', {
          payloadPhone: payloadPhone?.substring(0, 6) + '...',
          contactPhone: normalizedContactPhone.substring(0, 6) + '...',
          isTyping: payload.payload?.isTyping,
          isRecording: payload.payload?.isRecording
        });
        
        // Comparar telefones normalizados
        if (payloadPhone === normalizedContactPhone) {
          const isTyping = payload.payload.isTyping || false;
          const isRecording = payload.payload.isRecording || false;
          
          presenceLogger.info('Match de presença!', { isTyping, isRecording });
          setPresence({ isTyping, isRecording });
          
          // Limpar timeout existente
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          
          // Auto-reset após 5 segundos se não houver novo evento
          if (isTyping || isRecording) {
            timeoutRef.current = setTimeout(() => {
              presenceLogger.debug('Auto-reset de presença (timeout 5s)');
              setPresence({ isTyping: false, isRecording: false });
            }, 5000);
          }
        }
      })
      .subscribe();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [inboxId, normalizedContactPhone, providerSlug]);

  return presence;
}
